import { Run } from '../../runs/run.model';
import { RunStatus } from '../../runs/run-status.enum';
import { OptimisationStatus } from '../enums/optimisation-status.enum';

/** A base case: a plain timestamp run, as P&L Explain and the tender analysis pick them (RunNamingConventions.IsBaseCase). */
const baseCaseName = /^\d{4}-\d{2}-\d{2}_\d{6}( \(Local Run\))?$/i;

/** When a run last reached Success, or 0 if it never has. */
export const publishedAt = (run: Run) => Math.max(0, ...run.runHistories.filter(h => h.status === RunStatus.Success).map(h => new Date(h.eventTime).getTime()));

/** The latest Optimal base case and the one before it, by publish time: the pair P&L Explain opens with. */
export function latestBaseCases(runs: Run[]): { current: Run; prior?: Run } | null {
  const [current, prior] = runs
    .filter(r => baseCaseName.test(r.name) && r.optimisationResult?.status === OptimisationStatus.Optimal && publishedAt(r) > 0)
    .sort((a, b) => publishedAt(b) - publishedAt(a));
  return current ? { current, prior } : null;
}

export const isBaseCase = (name: string) => baseCaseName.test(name);

/** Who the tender analysis records as starting its reruns (TenderAnalysisWorker). */
const tenderWorker = 'Tender Analysis Worker';

/** "Rerun of 2026-10-27_154735: add …" → "2026-10-27_154735", the base case it reran (without "(Local Run)"). */
const rerunOf = (name: string) => /^Rerun of ([^:]+?)(:|$)/.exec(name)?.[1].trim();
const withoutLocal = (name: string) => name.replace(/ \(Local Run\)$/i, '');

/**
 * For each Optimal run, the run to compare its outputs with:
 * - a base case: the base case before it;
 * - a tender rerun: the base case it reran;
 * - anything else: the Optimal run published just before it.
 */
export function runsBefore(runs: Run[]): Map<string, string> {
  const optimal = runs
    .filter(r => r.optimisationResult?.status === OptimisationStatus.Optimal && publishedAt(r) > 0)
    .sort((a, b) => publishedAt(a) - publishedAt(b));
  const before = new Map<string, string>();
  const baseByName = new Map<string, Run>();
  let lastRun: Run | undefined, lastBase: Run | undefined;
  for (const r of optimal) {
    const base = isBaseCase(r.name);
    const stem = r.initiatedBy === tenderWorker ? rerunOf(r.name) : undefined;
    const pick = base ? lastBase : (stem && baseByName.get(stem)) || lastRun;
    if (pick) before.set(r.runId, pick.runId);
    lastRun = r;
    if (base) { lastBase = r; baseByName.set(withoutLocal(r.name), r); }
  }
  return before;
}
